import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { getEventPinConfig, getPlacePinConfig } from '../lib/pinConfig.js'

function PlanItemCard({ item, onRemove }) {
    const navigate = useNavigate()

    const categoryLabel = item?.category ?? (item?.searchType === 'places' ? 'Place' : 'Event')
    const pinConfig =
        item?.searchType === 'places'
            ? getPlacePinConfig(item?.category ?? item?.ml_category)
            : getEventPinConfig(item?.category ?? item?.ml_category)

    const handleClick = () => {
        const path = item?.searchType === 'places' ? `/place/${item.id}` : `/event/${item.id}`
        navigate(path)
    }

    const primaryImage = Array.isArray(item?.images)
        ? item.images[0]
        : item?.image || null

    return (
        <div className="group relative flex gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition hover:shadow-md">
            <button
                onClick={handleClick}
                className="flex flex-1 gap-2 text-left focus:outline-none cursor-pointer"
            >
                {primaryImage && (
                    <img
                        src={primaryImage}
                        alt={item?.name || 'Plan item'}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                    />
                )}
                <div className="flex flex-1 flex-col gap-1 min-w-0">
                    <div className="flex items-start gap-1">
                        <span
                            className={`text-base flex-shrink-0 ${item?.searchType === 'places' ? 'text-blue-600' : 'text-purple-600'}`}
                        >
                            {pinConfig.glyph}
                        </span>
                        <h4 className="flex-1 text-sm font-semibold text-slate-900 line-clamp-2">
                            {item?.name ?? item?.place_name ?? 'Untitled'}
                        </h4>
                    </div>
                    <p className="text-xs text-slate-500">
                        {categoryLabel}
                    </p>
                </div>
            </button>
            <button
                onClick={onRemove}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                title="Remove from plan"
            >
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    )
}

function SavedItemCard({ item, onRemove }) {
    const navigate = useNavigate()

    const isPlaceholder = item?.fromUrl && item?.searchType === 'unknown'
    const categoryLabel = isPlaceholder
        ? 'Saved Item'
        : item?.category ?? (item?.searchType === 'places' ? 'Place' : 'Event')
    const pinConfig =
        item?.searchType === 'places'
            ? getPlacePinConfig(item?.category ?? item?.ml_category)
            : getEventPinConfig(item?.category ?? item?.ml_category)

    const handleClick = () => {
        if (isPlaceholder) return
        const path = item?.searchType === 'places' ? `/place/${item.id}` : `/event/${item.id}`
        navigate(path)
    }

    const primaryImage = Array.isArray(item?.images)
        ? item.images[0]
        : item?.image || null

    return (
        <div className={`group relative flex gap-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm transition ${!isPlaceholder ? 'hover:shadow-md' : 'opacity-60'}`}>
            <button
                onClick={handleClick}
                disabled={isPlaceholder}
                className={`flex flex-1 gap-2 text-left focus:outline-none ${isPlaceholder ? 'cursor-default' : 'cursor-pointer'}`}
            >
                {primaryImage && (
                    <img
                        src={primaryImage}
                        alt={item?.name || 'Saved item'}
                        className="h-16 w-16 flex-shrink-0 rounded-lg object-cover"
                    />
                )}
                <div className="flex flex-1 flex-col gap-1 min-w-0">
                    <div className="flex items-start gap-1">
                        <span
                            className={`text-base flex-shrink-0 ${item?.searchType === 'places' ? 'text-blue-600' : isPlaceholder ? 'text-slate-400' : 'text-purple-600'}`}
                        >
                            {pinConfig.glyph}
                        </span>
                        <h4 className="flex-1 text-sm font-semibold text-slate-900 line-clamp-2">
                            {item?.name ?? item?.place_name ?? 'Untitled'}
                        </h4>
                    </div>
                    <p className="text-xs text-slate-500">
                        {categoryLabel}
                        {isPlaceholder && ' • Search to load details'}
                    </p>
                </div>
            </button>
            <button
                onClick={onRemove}
                className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                title="Remove from saved"
            >
                <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    )
}

function DateGroup({ date, items, onRemoveItem }) {
    // Format date nicely
    const formatDate = (dateStr) => {
        try {
            const dateObj = new Date(dateStr)
            const today = new Date()
            const tomorrow = new Date(today)
            tomorrow.setDate(tomorrow.getDate() + 1)

            // Reset time parts for comparison
            today.setHours(0, 0, 0, 0)
            tomorrow.setHours(0, 0, 0, 0)
            dateObj.setHours(0, 0, 0, 0)

            if (dateObj.getTime() === today.getTime()) {
                return 'Today'
            } else if (dateObj.getTime() === tomorrow.getTime()) {
                return 'Tomorrow'
            } else {
                return dateObj.toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    year: dateObj.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
                })
            }
        } catch {
            return dateStr
        }
    }

    return (
        <div className="space-y-2">
            <h3 className="flex items-center gap-2 text-sm font-bold text-slate-700 border-b border-slate-200 pb-1">
                <span>📅</span>
                <span>{formatDate(date)}</span>
                <span className="ml-auto text-xs font-normal text-slate-500">
                    {items.length} {items.length === 1 ? 'item' : 'items'}
                </span>
            </h3>
            <div className="space-y-2">
                {items.map(item => (
                    <PlanItemCard
                        key={`${item.id}-${item.plannedDate}-${item.planId}`}
                        item={item}
                        onRemove={() => onRemoveItem(item.id, item.planId, item.plannedDate)}
                    />
                ))}
            </div>
        </div>
    )
}

export default function TravelingPlanSidebar({
    plans,                  // Array of plan objects with { id, name, color }
    planByDate,             // Function that takes planId and returns grouped items
    onRemoveItem,           // Function(itemId, planId, date)
    onClearPlan,            // Function(planId)
    onCreatePlan,           // Function(name, color) => newPlan
    onUpdatePlan,           // Function(planId, updates)
    onDeletePlan,           // Function(planId)
    onShowCreateModal,      // Function() - callback to show create modal at parent level
    onShowEditModal,        // Function(plan) - callback to show edit modal at parent level
    savedItems,
    onRemoveSaved,
    isCollapsed,
    onToggleCollapse
}) {
    const [activeTab, setActiveTab] = useState(plans[0]?.id || 'likes')
    const [planMenuOpen, setPlanMenuOpen] = useState(null) // planId of the plan with open menu

    const totalSavedItems = savedItems.length

    // Get item count for a specific plan
    const getPlanItemCount = (planId) => {
        const grouped = planByDate(planId)
        return grouped.reduce((sum, group) => sum + group.items.length, 0)
    }

    const handleTabClick = (tabId) => {
        if (isCollapsed) {
            // If collapsed, open and switch to this tab
            setActiveTab(tabId)
            onToggleCollapse()
        } else if (activeTab === tabId) {
            // If clicking the active tab, collapse the sidebar
            onToggleCollapse()
        } else {
            // If clicking a different tab, just switch tabs
            setActiveTab(tabId)
        }
    }

    const handleCreatePlanClick = () => {
        onShowCreateModal()
    }

    const handleEditPlanClick = (plan) => {
        onShowEditModal(plan)
        setPlanMenuOpen(null)
    }

    const handleDeletePlan = (planId) => {
        if (confirm('Are you sure you want to delete this plan? All items in this plan will be removed.')) {
            onDeletePlan(planId)
            setPlanMenuOpen(null)
            // Switch to first remaining plan or likes
            const remainingPlans = plans.filter(p => p.id !== planId)
            setActiveTab(remainingPlans[0]?.id || 'likes')
        }
    }

    // Find active plan or determine if likes tab is active
    const activePlan = plans.find(p => p.id === activeTab)
    const isLikesTab = activeTab === 'likes'
    const activePlanItems = activePlan ? planByDate(activePlan.id) : []
    const activePlanItemCount = activePlan ? getPlanItemCount(activePlan.id) : 0

    return (
        <div className={`relative h-full transition-all duration-300 ${isCollapsed ? 'w-0' : 'w-80'}`}>
            {/* Collapsible sidebar with tabs - entire container moves together */}
            <div className="flex h-full">
                {/* Collapsible sidebar content */}
                <div
                    className={`flex h-full flex-col bg-white border-r border-slate-200 shadow-lg transition-all duration-300 ${
                        isCollapsed ? 'w-0 -translate-x-80' : 'w-80 translate-x-0'
                    }`}
                    style={{ overflow: isCollapsed ? 'hidden' : 'visible' }}
                >
                {/* Header */}
                <div className="flex items-center justify-between border-b border-slate-200 p-4 bg-slate-50">
                    <h2 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                        {isLikesTab ? (
                            <>
                                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z" clipRule="evenodd" />
                                </svg>
                                <span>Saved Items</span>
                            </>
                        ) : activePlan ? (
                            <>
                                <div
                                    className="w-4 h-4 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: activePlan.color }}
                                />
                                <span>{activePlan.name}</span>
                                {/* Plan menu button */}
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        setPlanMenuOpen(planMenuOpen === activePlan.id ? null : activePlan.id)
                                    }}
                                    className="ml-auto p-1 hover:bg-slate-200 rounded transition"
                                    title="Plan options"
                                >
                                    <svg className="w-5 h-5 text-slate-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                                    </svg>
                                </button>

                                {/* Plan dropdown menu */}
                                {planMenuOpen === activePlan.id && (
                                    <div className="absolute right-4 top-14 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 min-w-[160px]">
                                        <button
                                            onClick={() => handleEditPlanClick(activePlan)}
                                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2"
                                        >
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            Edit Plan
                                        </button>
                                        {plans.length > 1 && (
                                            <button
                                                onClick={() => handleDeletePlan(activePlan.id)}
                                                className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
                                            >
                                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                                Delete Plan
                                            </button>
                                        )}
                                    </div>
                                )}
                            </>
                        ) : null}
                    </h2>
                </div>

                {/* Content with transition */}
                <div className="flex-1 overflow-y-auto p-4">
                    <div key={activeTab} className="animate-fadeIn">
                        {isLikesTab ? (
                            totalSavedItems === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                                    <svg className="h-16 w-16 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <div>
                                        <p className="text-base font-medium text-slate-900">No saved items yet</p>
                                        <p className="mt-1 text-sm text-slate-500">
                                            Save events and places to view them here
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {savedItems.map(item => (
                                        <SavedItemCard
                                            key={item.id}
                                            item={item}
                                            onRemove={() => onRemoveSaved(item.id)}
                                        />
                                    ))}
                                </div>
                            )
                        ) : activePlan ? (
                            activePlanItemCount === 0 ? (
                                <div className="flex flex-col items-center justify-center gap-4 py-12 text-center">
                                    <span className="text-6xl opacity-30">📅</span>
                                    <div>
                                        <p className="text-base font-medium text-slate-900">No items in this plan</p>
                                        <p className="mt-1 text-sm text-slate-500">
                                            Add events and places to build your itinerary
                                        </p>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {activePlanItems.map(group => (
                                        <DateGroup
                                            key={group.date}
                                            date={group.date}
                                            items={group.items}
                                            onRemoveItem={onRemoveItem}
                                        />
                                    ))}
                                </div>
                            )
                        ) : null}
                    </div>
                </div>

                {/* Footer with count and clear button */}
                {((activePlan && activePlanItemCount > 0) || (isLikesTab && totalSavedItems > 0)) && (
                    <div className="border-t border-slate-200 p-3 bg-slate-50 flex items-center justify-between">
                        <p className="text-xs text-slate-600">
                            {isLikesTab
                                ? `${totalSavedItems} ${totalSavedItems === 1 ? 'item' : 'items'} saved`
                                : `${activePlanItemCount} ${activePlanItemCount === 1 ? 'item' : 'items'} in plan`
                            }
                        </p>
                        {activePlan && activePlanItemCount > 0 && (
                            <button
                                onClick={() => onClearPlan(activePlan.id)}
                                className="text-xs text-slate-500 hover:text-red-600 transition"
                                title="Clear all items from this plan"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                )}
            </div>

                {/* Sidebar tabs - positioned on right edge, move with the sidebar */}
                <div className="flex flex-col gap-2 z-20 py-4" style={{ paddingTop: '5rem' }}>
                    {/* Dynamic plan tabs */}
                    {plans.map((plan) => {
                        const itemCount = getPlanItemCount(plan.id)
                        const isActive = activeTab === plan.id && !isCollapsed

                        return (
                            <button
                                key={plan.id}
                                onClick={() => handleTabClick(plan.id)}
                                className={`transition-all rounded-r-lg shadow-lg px-2 hover:px-3 py-4 ${
                                    isActive
                                        ? 'text-white hover:opacity-90'
                                        : 'text-white opacity-80 hover:opacity-100'
                                }`}
                                style={{ backgroundColor: plan.color }}
                                title={plan.name}
                            >
                                <div className="flex flex-col items-center gap-1">
                                    <span className="text-xs font-bold writing-mode-vertical transform rotate-180 max-w-[12px] truncate" style={{ writingMode: 'vertical-rl' }}>
                                        {plan.name.toUpperCase().slice(0, 12)}
                                    </span>
                                    <span className="bg-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1"
                                        style={{ color: plan.color }}
                                    >
                                        {itemCount}
                                    </span>
                                </div>
                            </button>
                        )
                    })}

                    {/* Create new plan button */}
                    <button
                        onClick={(e) => {
                            e.stopPropagation()
                            handleCreatePlanClick()
                        }}
                        className="transition-all rounded-r-lg shadow-lg bg-gray-400 text-white hover:bg-gray-500 px-2 hover:px-3 py-3"
                        title="Create new plan"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                    </button>

                    {/* Likes tab */}
                    <button
                        onClick={() => handleTabClick('likes')}
                        className={`transition-all rounded-r-lg shadow-lg ${
                            activeTab === 'likes' && !isCollapsed
                                ? 'bg-red-500 text-white hover:bg-red-600 px-2 hover:px-3 py-4'
                                : 'bg-red-500/80 text-white hover:bg-red-600 px-2 hover:px-3 py-4'
                        }`}
                        title="Likes"
                    >
                        <div className="flex flex-col items-center gap-1">
                            <span className="text-xs font-bold writing-mode-vertical transform rotate-180" style={{ writingMode: 'vertical-rl' }}>
                                LIKES
                            </span>
                            <span className="bg-white text-red-500 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mt-1">
                                {totalSavedItems}
                            </span>
                        </div>
                    </button>
                </div>
            </div>
        </div>
    )
}
